import { Checkbox, Status, type VirtualTableColumn } from "@snapchat/snap-design-system";

import { ActionDisabledTooltip } from "../../../common/components/ActionDisabledTooltip";
import { Header } from "../../../common/components/table/Header";
import { TwoLineCell } from "../../../common/components/table/TwoLineCell";
import { DecreaseText } from "../../../common/components/text";
import { formatMediumDateTime } from "../../../common/formatters";
import { type GroupsGridItemModel } from "../../grid/model";

import { Right } from "./AddToLensGroupTableColumns.styles";
import type { TagType } from "../../../common/components/TagWithTooltip";
import type { GroupTags } from "../groups-table/types";

// Unfortunately SDS doesn't export this interface
interface CellRenderer {
    row: {
        index: number;
    };
    data: Readonly<GroupTableItem[]>;
}

function getRenderer(fn: (item: GroupTableItem) => JSX.Element) {
    return ({ row, data }: CellRenderer) => fn(data[row.index] as GroupTableItem);
}

export interface GroupTableItem {
    group: GroupsGridItemModel;
    selected: boolean;
    alreadySelected: boolean;
}

interface AddLensToGroupTableProps {
    newGroups: ReadonlySet<string>;
    onToggleSelection: (group: GroupsGridItemModel) => void;
}

export function getColumns({
    onToggleSelection,
    newGroups,
}: AddLensToGroupTableProps): VirtualTableColumn<GroupTableItem>[] {
    return [
        {
            id: "checkbox",
            width: 50,
            Cell: getRenderer(({ group, selected, alreadySelected }) => (
                <Right>
                    <ActionDisabledTooltip placement="top">
                        <Checkbox
                            onChange={() => {
                                onToggleSelection(group);
                            }}
                            value={alreadySelected || selected}
                            disabled={alreadySelected}
                        />
                    </ActionDisabledTooltip>
                </Right>
            )),
        },
        {
            id: "name",
            Header: <Header>Name / ID</Header>,
            width: 350,
            Cell: getRenderer(({ group }) => {
                const tags = newGroups.has(group.id as string)
                    ? [
                          {
                              status: Status.INFO,
                              text: "New",
                          } as TagType<GroupTags>,
                      ]
                    : undefined;
                return <TwoLineCell text={group.name as string} subText={group.id as string} tags={tags} />;
            }),
        },
        {
            id: "lastUpdated",
            width: 300,
            Header: <Header>Last Updated</Header>,
            Cell: getRenderer(({ group }) => (
                <DecreaseText>{formatMediumDateTime(group.lastUpdated as Date)}</DecreaseText>
            )),
        },
    ];
}
